import { useContext } from "react";
import { CartContext } from "../context/CartContext";

// Normalize any inbound product/cart item to expected internal shape
function normalizeIncoming(item) {
  if (!item) return null;
  // Support both legacy keys (id, name, image, price) and new ones (ProductId, Name, Image, Price)
  const ProductId = item.ProductId ?? item.id ?? item.Id ?? item.ID;
  const Name =
    item.Name ?? item.name ?? item.title ?? item.Title ?? "Unnamed Product";
  const Image =
    item.Image ??
    item.image ??
    item.img ??
    item.Img ??
    "assets/img/placeholder.png";
  const Price = Number(item.Price ?? item.price ?? 0); // ensure number
  // IMPORTANT: Do NOT use product.catalog 'quantity' (stock) as cart quantity; always start at 1 unless an existing
  // cart-shaped item explicitly provides Quantity.
  const Quantity = Number(item.Quantity ?? 1);
  // Total is derived; reducer will also recalc but we set here for immediate UI usage
  const Total = Price * Quantity;
  return { ProductId, Name, Image, Price, Quantity, Total };
}

export function useCart() {
  const { cart, dispatch } = useContext(CartContext);

  const addItem = (raw) => {
    const normalized = normalizeIncoming(raw);
    if (!normalized || normalized.ProductId == null) return; // ignore invalid
    dispatch({ type: "ADD_ITEM", payload: normalized });
  };

  const updateItem = (ProductId, updates) => {
    dispatch({ type: "UPDATE_ITEM", payload: { ProductId, updates } });
  };

  const updateQty = (ProductId, nextQty) => {
    if (nextQty < 1) return; // enforce minimum 1
    updateItem(ProductId, { Quantity: nextQty });
  };

  const removeItem = (ProductId) => {
    dispatch({ type: "REMOVE_ITEM", payload: { ProductId } });
  };

  const clearCart = () => dispatch({ type: "EMPTY_CART" });
  const emptyCart = clearCart; // alias

  // Derive totals safely (guard against malformed entries)
  const totalItems = cart.reduce(
    (sum, item) => sum + (Number(item.Quantity) || 0),
    0,
  );
  const totalPrice = cart.reduce(
    (sum, item) =>
      sum + (Number(item.Total ?? item.Price * item.Quantity) || 0),
    0,
  );

  // Backward compatible aliases expected by existing components
  const items = cart; // old name
  const subtotal = totalPrice; // alias

  return {
    // primary modern names
    cart,
    addItem,
    updateItem,
    removeItem,
    emptyCart,
    totalItems,
    totalPrice,
    // legacy / convenience API
    items,
    updateQty,
    clearCart,
    subtotal,
  };
}
